import { APP_ERROR_OUTCOME } from '../conventions';
import type { ClientErrorPayload } from './types';

const REPORT_ERROR_ENDPOINT = '/api/report-error';
const MAX_MESSAGE_LENGTH = 200;
const STALE_CHUNK_RELOAD_KEY = 'highlight-stale-chunk-reload';

/**
 * Script URL schemes used by browser extensions and in-app browsers (e.g. `iabjs://` from the
 * Android Facebook/Instagram webview). Errors thrown by these scripts are not ours to fix.
 */
const THIRD_PARTY_SCRIPT_URL_PREFIXES = [
  'iabjs://',
  'chrome-extension://',
  'moz-extension://',
  'safari-extension://',
  'safari-web-extension://',
  'webkit-masked-url://',
  'ms-browser-extension://',
];

let currentUserId: string | undefined;

/**
 * Sets the current user id for client error reports. Call from a hook-based
 * bridge when the session resolves; pass null/undefined to clear.
 */
export function setClientErrorUserId(userId: string | null | undefined): void {
  currentUserId = userId ?? undefined;
}

function truncateMessage(message: string | undefined): string | undefined {
  if (!message) return undefined;
  return message.length > MAX_MESSAGE_LENGTH ? message.slice(0, MAX_MESSAGE_LENGTH) : message;
}

/** Benign browser errors that should not be reported to production monitoring. */
function isBenignWindowError(message: string): boolean {
  return (
    message.includes('ResizeObserver loop completed with undelivered notifications') ||
    message.includes('ResizeObserver loop limit exceeded')
  );
}

/**
 * Unique V8 TypeError (`(reading 'M_ID')`) with no first-party `M_ID` identifier in
 * ClientUi. Prod hits are stackless unhandled rejections on highlighter-app, consistent
 * with its third-party wrappers rather than app code. Match the property access
 * exactly so generic `Cannot read properties of undefined` TypeErrors still report.
 */
function isThirdPartyMIdError(message: string): boolean {
  return message.includes("(reading 'M_ID')");
}

/**
 * WebKit TypeError thrown by a crypto-wallet script that an in-app browser or extension
 * injects into the page (single `global code@<page>:1:16` frame, no `_next/static` frames).
 * ClientUi has no first-party `window.ethereum` usage. Match the exact assignment so other
 * `undefined is not an object` TypeErrors still report.
 */
function isInjectedWalletError(message: string): boolean {
  return message.includes("(evaluating 'window.ethereum.selectedAddress = undefined')");
}

function isThirdPartyScriptError(filename?: string): boolean {
  const normalized = filename?.toLowerCase();
  return (
    normalized !== undefined &&
    THIRD_PARTY_SCRIPT_URL_PREFIXES.some((prefix) => normalized.startsWith(prefix))
  );
}

/**
 * After a deploy, cached HTML can reference old `_next/static/chunks/*` hashes. The server
 * returns an HTML 404 and the browser throws `SyntaxError: Unexpected token '<'` at line 1.
 */
function isStaleNextChunkError(message: string, filename?: string): boolean {
  return (
    message.includes("Unexpected token '<'") &&
    filename !== undefined &&
    filename.includes('/_next/static/chunks/')
  );
}

function tryReloadForStaleChunk(): boolean {
  try {
    if (sessionStorage.getItem(STALE_CHUNK_RELOAD_KEY)) {
      return false;
    }

    sessionStorage.setItem(STALE_CHUNK_RELOAD_KEY, '1');
    window.location.reload();
    return true;
  } catch {
    return false;
  }
}

function isAbortError(reason: unknown): boolean {
  if (reason instanceof DOMException) {
    return reason.name === 'AbortError';
  }

  return reason instanceof Error && reason.name === 'AbortError';
}

/**
 * Intentional Pages Router navigations aborted by leave-confirmation (and
 * similar) throw a marked Error with `cancelled: true`. Next leaves that throw
 * as an unhandled rejection from `<Link>`/`router.push`, so we skip reporting.
 *
 * Fetch/RTK Query aborts (navigation, unmount, dedupe) also surface as
 * `AbortError` / "signal is aborted without reason" unhandled rejections.
 */
function isBenignUnhandledRejection(reason: unknown): boolean {
  if (isAbortError(reason)) {
    return true;
  }

  return (
    typeof reason === 'object' &&
    reason !== null &&
    'cancelled' in reason &&
    (reason as { cancelled?: unknown }).cancelled === true
  );
}

/**
 * Reports a client-side error to the app via beacon (falls back to fetch with keepalive).
 */
export function reportClientError(payload: ClientErrorPayload): void {
  if (typeof window === 'undefined') {
    return;
  }

  const userId = payload.userId ?? currentUserId;
  const pathname = payload.pathname ?? window.location.pathname;
  const errorName = payload.errorName;
  const stack = payload.stack;
  const filenameLinenoColno = payload.filenameLinenoColno;

  const body = JSON.stringify({
    outcome: payload.outcome,
    ...(payload.component ? { component: payload.component } : {}),
    ...(payload.message ? { message: truncateMessage(payload.message) } : {}),
    ...(userId ? { userId } : {}),
    ...(pathname ? { pathname } : {}),
    ...(errorName ? { errorName } : {}),
    ...(stack ? { stack } : {}),
    ...(filenameLinenoColno ? { filenameLinenoColno } : {}),
  });

  if (navigator.sendBeacon) {
    navigator.sendBeacon(REPORT_ERROR_ENDPOINT, new Blob([body], { type: 'application/json' }));
    return;
  }

  void fetch(REPORT_ERROR_ENDPOINT, {
    method: 'POST',
    body,
    headers: { 'Content-Type': 'application/json' },
    keepalive: true,
  });
}

/**
 * Registers global handlers for uncaught errors outside React's tree.
 *
 * @returns Cleanup function to remove listeners (for tests or strict mode).
 */
export function setupGlobalClientErrorHandlers(): () => void {
  if (typeof window === 'undefined') {
    return () => undefined;
  }

  const onWindowError = (event: ErrorEvent) => {
    if (
      isBenignWindowError(event.message) ||
      isThirdPartyMIdError(event.message) ||
      isInjectedWalletError(event.message) ||
      isThirdPartyScriptError(event.filename)
    ) {
      return;
    }

    if (isStaleNextChunkError(event.message, event.filename)) {
      if (tryReloadForStaleChunk()) {
        return;
      }
    }

    reportClientError({
      outcome: APP_ERROR_OUTCOME.WINDOW_ERROR,
      message: event.message,
      errorName: event.error instanceof Error ? event.error.name : undefined,
      stack: event.error instanceof Error ? event.error.stack : undefined,
      filenameLinenoColno: `${event.filename}:${event.lineno}:${event.colno}`,
    });
  };

  const onUnhandledRejection = (event: PromiseRejectionEvent) => {
    const reason = event.reason;
    if (isBenignUnhandledRejection(reason)) {
      return;
    }

    const message =
      reason instanceof Error
        ? reason.message
        : typeof reason === 'string'
          ? reason
          : 'unhandled_rejection';

    if (isThirdPartyMIdError(message) || isInjectedWalletError(message)) {
      return;
    }

    reportClientError({
      outcome: APP_ERROR_OUTCOME.UNHANDLED_REJECTION,
      message,
      errorName: reason instanceof Error ? reason.name : undefined,
    });
  };

  window.addEventListener('error', onWindowError);
  window.addEventListener('unhandledrejection', onUnhandledRejection);

  return () => {
    window.removeEventListener('error', onWindowError);
    window.removeEventListener('unhandledrejection', onUnhandledRejection);
  };
}
